import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { columns as buildColumns, SORT_KEY_MAP } from './constants';
import QuickEntryRow from './QuickEntryRow';
import InlineEditTable from '../../../../core/components/InlineEditTable/InlineEditTable';
import {
  selectProjectTasks,
  selectProjectTotal,
  selectProjectPage,
  selectProjectPageSize,
} from '../../reducer';
import { selectEmployees } from '../../../employees/reducer';
import { selectTaskPrefixes } from '../../reducer';
import { selectSession, selectSite } from '../../../../store/authReducer';
import { TaskCompletionStatus } from '../../types';
import { DATE_FORMATS, PRIORITY_LABEL_TO_VALUE } from '../QuickTasks/constants';
import { toString } from '../../../../core/utils';
import * as helpers from '../QuickTasks/helpers';
import { useIntl } from 'react-intl';
import { selectCurrentEmployee } from '../../reducer';
import { saveTask, fetchProjectTasks, addTask } from '../../actions';
import { TaskType, Interval, ProjectStatus } from '../../models';
import { getApiStatus } from '../../../../core/utils';
import { updateTaskCompletionStatus } from '../../../overview/actions';
import * as toastMessages from '../../../../core/toastMessages';
import { useOptimisticList } from '../../../../core/hooks/useOptimisticList';
import { SortState } from '../../../../core/components/InlineEditTable/SearchTable/types';
import {
  addPermissionRefs,
  useHasUserGroupPermission,
} from '../../../../core/components/UserGroupPermissions';

type WorkOrderTableProps = {
  headerActions?: React.ReactNode;
  onOpenTask?: (task: any) => void;
  highlightedRow?: string | number;
  externalFilters?: Record<string, any>;
  filterBarVisible?: boolean;
  // Bumped by the parent to force a refetch with the current filters/sort/page
  refreshToken?: number;
};

function WorkOrderTable({
  headerActions,
  onOpenTask,
  highlightedRow,
  externalFilters,
  refreshToken = 0,
}: WorkOrderTableProps) {
  const intl = useIntl();
  const [sortQuery, setSortQuery] = useState<SortState | null>(null);
  // The quick-entry row writes work orders, so it needs the same permission as
  // the Add Work Order button rather than failing at the API.
  const canAddWorkOrder = useHasUserGroupPermission(addPermissionRefs.task);
  const [entryResetToken, setEntryResetToken] = useState(0);
  const isCreatingRef = useRef(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    nameQuery: '',
    dateQuery: '',
    createdDateQuery: '',
    statusFilter: [],
    priorityFilter: [],
    memberFilter: [],
    prefixFilter: [],
    locationFilter: [],
  });

  const tasks = useSelector(selectProjectTasks) || [];
  const employees = useSelector(selectEmployees) || [];
  const taskPrefixList = useSelector(selectTaskPrefixes) || [];
  const session = useSelector(selectSession);
  const siteId = useSelector(selectSite);
  const currentEmployee = useSelector(selectCurrentEmployee);

  const workOrderPage = useSelector(selectProjectPage);
  const workOrderPageSize = useSelector(selectProjectPageSize);
  const workOrderTotal = useSelector(selectProjectTotal);
  const isLoading = useSelector((state: any) => getApiStatus(state, 'FETCH_PROJECT_TASKS'));
  const dispatch = useDispatch();
  const timeZone = session?.timeZone || sessionStorage.getItem('timeZone') || moment.tz.guess();

  const {
    localList: localTasks,
    updateItem: updateLocalTask,
    restoreItem: restoreLocalTask,
    listRef: localTasksRef,
  } = useOptimisticList(tasks, {
    compareFields: [
      'name',
      'priority',
      'employeeId',
      'end',
      'start',
      'description',
      'desiredStatus',
      'latestCompletion',
    ],
  });

  const handleSort = useCallback((keyName: string) => {
    setSortQuery((prev) => ({
      keyName,
      direction: prev?.keyName === keyName && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const buildWorkOrdersQuery = useCallback(
    (page = 0) => {
      const query: Record<string, any> = { page };
      if (filterValues.nameQuery) query.search = filterValues.nameQuery;
      if (filterValues.prefixFilter?.length) query.prefix = filterValues.prefixFilter;
      if (filterValues.statusFilter?.length)
        query.status = filterValues.statusFilter.map((s: string) =>
          helpers.mapDisplayStatusToApiStatus(s)
        );
      if (filterValues?.memberFilter?.length && filterValues.memberFilter !== 'All') {
        const employeeIds = filterValues.memberFilter.map((m: string) =>
          m === 'Unassigned' ? 0 : Number(m)
        );
        query.employeeId = employeeIds;
      }
      if (filterValues.locationFilter?.length) query.siteIds = filterValues.locationFilter;
      if (filterValues.dateQuery) {
        query.dueDateFrom = moment
          .tz(filterValues.dateQuery, timeZone)
          .startOf('day')
          .toISOString();
        query.dueDateUntil = moment.tz(filterValues.dateQuery, timeZone).endOf('day').toISOString();
      }
      if (filterValues.createdDateQuery) {
        query.from = moment
          .tz(filterValues.createdDateQuery, timeZone)
          .startOf('day')
          .toISOString();
        query.until = moment.tz(filterValues.createdDateQuery, timeZone).endOf('day').toISOString();
      }
      if (sortQuery?.keyName) {
        query.key = SORT_KEY_MAP[sortQuery.keyName] ?? sortQuery.keyName;
        query.direction = sortQuery.direction;
      }
      return query;
    },
    [filterValues, sortQuery, timeZone]
  );

  const fetchWorkOrders = useCallback(
    (page = 0) => {
      dispatch(fetchProjectTasks(buildWorkOrdersQuery(page)));
    },
    [dispatch, buildWorkOrdersQuery]
  );

  useEffect(() => {
    fetchWorkOrders(0);
  }, [fetchWorkOrders, refreshToken]);

  const handlePriorityChange = useCallback(
    (row: Record<string, any>, nextLabel: string) => {
      const next = PRIORITY_LABEL_TO_VALUE[nextLabel];
      if (next === undefined) return;
      const previous = localTasksRef.current.find((task: any) => task.id === row.id);
      updateLocalTask(row.id, { priority: next });
      dispatch(
        saveTask({
          task: { id: row.id, priority: next },
          callback: () => {},
          errorCallback: (err: unknown) => {
            const errorObj = err as { statusCode?: number; responseStatus?: number };
            if (!errorObj?.statusCode && !errorObj?.responseStatus) {
              toastMessages.apiError(err);
            }
            restoreLocalTask(previous);
          },
        })
      );
    },
    [dispatch, updateLocalTask, restoreLocalTask]
  );

  const handleMemberChange = useCallback(
    (row: Record<string, any>, nextValue: string | number | '') => {
      const employeeId =
        nextValue === undefined || nextValue === null || nextValue === ''
          ? null
          : Number(nextValue);
      const previous = localTasksRef.current.find((task: any) => task.id === row.id);
      updateLocalTask(row.id, { employeeId });
      dispatch(
        saveTask({
          task: { id: row.id, employeeId },
          callback: () => {},
          errorCallback: (err: unknown) => {
            const errorObj = err as { statusCode?: number; responseStatus?: number };
            if (!errorObj?.statusCode && !errorObj?.responseStatus) {
              toastMessages.apiError(err);
            }
            restoreLocalTask(previous);
          },
        })
      );
    },
    [dispatch, updateLocalTask, restoreLocalTask]
  );

  const handleStatusChange = useCallback(
    (row: Record<string, any>, nextLabel: string) => {
      const apiStatus = helpers.mapDisplayStatusToApiStatus(nextLabel);
      const current = localTasks.find((task) => task.id === row.id);
      const previous = localTasksRef.current.find((task: any) => task.id === row.id);
      const latestCompletion = current?.latestCompletion
        ? { ...current.latestCompletion, status: nextLabel }
        : { status: nextLabel };
      updateLocalTask(row.id, { desiredStatus: nextLabel, latestCompletion });
      if (latestCompletion?.id) {
        dispatch(
          updateTaskCompletionStatus({
            options: { siteId },
            taskIds: [latestCompletion?.id],
            status: apiStatus,
            employeeId: currentEmployee,
            notes: null,
            errorCallback: (err: unknown) => {
              const errorObj = err as { statusCode?: number; responseStatus?: number };
              if (!errorObj?.statusCode && !errorObj?.responseStatus) {
                toastMessages.apiError(err);
              }
              restoreLocalTask(previous);
            },
          })
        );
      }
    },
    [dispatch, siteId, currentEmployee, updateLocalTask, localTasks, restoreLocalTask]
  );

  const handleDueDateChange = useCallback(
    (row: Record<string, any>, date: Date | null) => {
      if (!date) return;
      const iso = moment(date).toISOString();
      const previous = localTasksRef.current.find((task: any) => task.id === row.id);
      const field = previous?.end ? 'end' : 'start';
      updateLocalTask(row.id, { [field]: iso });
      dispatch(
        saveTask({
          task: { id: row.id, [field]: iso },
          callback: () => {},
          errorCallback: (err: unknown) => {
            const errorObj = err as { statusCode?: number; responseStatus?: number };
            if (!errorObj?.statusCode && !errorObj?.responseStatus) {
              toastMessages.apiError(err);
            }
            restoreLocalTask(previous);
          },
        })
      );
    },
    [dispatch, updateLocalTask, restoreLocalTask]
  );

  const handleCellCommit = useCallback(
    (payload: {
      row: Record<string, any>;
      keyName: string;
      value: any;
      mode: 'change' | 'commit';
    }) => {
      const { row, keyName, value, mode } = payload;
      if (keyName !== 'name') return;

      if (mode === 'change') {
        updateLocalTask(row.id, { [keyName]: value });
        return;
      }

      if (mode === 'commit') {
        const current = localTasksRef.current.find((t: any) => t.id === row.id);
        const previous = current ? { ...current } : null;
        const currentValue = current?.name ?? '';
        if (currentValue === value) return;

        updateLocalTask(row.id, { [keyName]: value });
        dispatch(
          saveTask({
            task: { id: row.id, [keyName]: value },
            callback: () => {},
            errorCallback: (err: unknown) => {
              const errorObj = err as { statusCode?: number; responseStatus?: number };
              if (!errorObj?.statusCode && !errorObj?.responseStatus) {
                toastMessages.apiError(err);
              }
              restoreLocalTask(previous);
            },
          })
        );
      }
    },
    [dispatch, updateLocalTask, restoreLocalTask]
  );

  const handleCreateWorkOrder = useCallback(
    (values: Record<string, any>) => {
      // A create is already in flight; ignore the repeat submit.
      if (isCreatingRef.current) return false;
      const name = String(values?.name || '').trim();
      // Keep the entry row populated if there's no name yet.
      if (!name) return false;
      const description = values?.description ? String(values.description).trim() : null;
      const due =
        values?.dueDate instanceof Date
          ? moment(values.dueDate).toISOString()
          : moment().toISOString();
      const priority =
        values?.priority && PRIORITY_LABEL_TO_VALUE[values.priority] !== undefined
          ? PRIORITY_LABEL_TO_VALUE[values.priority]
          : PRIORITY_LABEL_TO_VALUE.Medium;
      const employeeId =
        values?.teamMember === undefined || values?.teamMember === null || values?.teamMember === ''
          ? null
          : Number(values.teamMember);
      const prefixId = values?.prefix ? Number(values.prefix) : undefined;
      const task: Record<string, any> = {
        name,
        description,
        start: due,
        end: due,
        prefixId,
        priority,
        employeeId,
        type: TaskType.Project,
        interval: Interval.Once,
        quickAddTask: true,
      };
      if (values?.status) {
        task.desiredStatus = values.status;
      }
      // In Progress and Completed are backed by a task completion, which the
      // ADD_TASK_SUCCESS saga creates off meta.desiredStatus. New and On Hold
      // are carried by the posted desiredStatus alone.
      const needsCompletion =
        values?.status === ProjectStatus.InProgress || values?.status === ProjectStatus.Completed;

      isCreatingRef.current = true;
      dispatch(
        addTask({
          task,
          meta: needsCompletion ? { desiredStatus: values.status } : undefined,
          // Refresh the table once the POST succeeds so the new work order
          // appears, and only then clear what the user typed.
          callback: () => {
            isCreatingRef.current = false;
            setEntryResetToken((token) => token + 1);
            return fetchProjectTasks(buildWorkOrdersQuery(0));
          },
          errorCallback: () => {
            // Leave the row populated so the entry can be retried.
            isCreatingRef.current = false;
          },
        })
      );
      // Hold the row until the POST lands; the callback clears it.
      return false;
    },
    [dispatch, buildWorkOrdersQuery]
  );

  const columnOptions = {
    intl,
    employees,
    prefixes: taskPrefixList,
    onMemberChange: handleMemberChange,
    onPriorityChange: handlePriorityChange,
    onStatusChange: handleStatusChange,
    onDueDateChange: handleDueDateChange,
  };

  const tableColumns = buildColumns(columnOptions);

  useEffect(() => {
    if (!externalFilters) return;
    setFilterValues((prev) => ({ ...prev, ...externalFilters }));
  }, [externalFilters]);

  const statusDisplayForWorkOrder = (task: any) => {
    if (task?.latestCompletion?.status !== undefined && task?.latestCompletion?.status !== null) {
      const raw = task.latestCompletion.status;
      const statusText =
        typeof raw === 'number' ? toString(TaskCompletionStatus, raw) : String(raw);
      return helpers.normalizeProjectStatus(statusText);
    }
    if (task && (task.desiredStatus || task.desired_status)) {
      return helpers.normalizeProjectStatus(task.desiredStatus || task.desired_status);
    }
    return 'New';
  };

  const formatInTimeZone = (value: any) => {
    if (!value) return null;
    try {
      return moment.tz(value, timeZone);
    } catch (e) {
      return moment(value);
    }
  };

  const rows = useMemo(() => {
    const statusFilter = filterValues.statusFilter;
    const priorityFilter = filterValues.priorityFilter;
    const nameQuery = filterValues.nameQuery || '';
    const dateQuery = filterValues.dateQuery || '';
    const createdDateQuery = filterValues.createdDateQuery || '';
    const memberFilter = filterValues.memberFilter || [];
    const prefixFilter = filterValues.prefixFilter || [];
    const locationFilter = filterValues.locationFilter || [];

    const filteredWorkOrders = localTasks.filter((task: any) => {
      const statusText = statusDisplayForWorkOrder(task);
      return (
        helpers.passesName(task, nameQuery) &&
        helpers.passesDate(task, dateQuery) &&
        helpers.passesCreatedDate(task, createdDateQuery) &&
        helpers.passesMember(task, Array.isArray(memberFilter) ? memberFilter : []) &&
        helpers.passesPrefix(task, Array.isArray(prefixFilter) ? prefixFilter.map(String) : []) &&
        helpers.passesPriority(task, Array.isArray(priorityFilter) ? priorityFilter : []) &&
        helpers.passesStatus(statusText, Array.isArray(statusFilter) ? statusFilter : []) &&
        helpers.passesLocation(task.siteId, Array.isArray(locationFilter) ? locationFilter : [])
      );
    });

    return filteredWorkOrders.map((task: any) => {
      const dueMoment = formatInTimeZone(task?.end || task?.start);
      const createdMoment = formatInTimeZone(task?.createdAt);
      const employee = employees.find((e: any) => String(e.id) === String(task?.employeeId));
      const employeeLabel = employee ? `${employee.lastName}, ${employee.firstName}` : '';
      const prefixMatch = taskPrefixList.find(
        (p: any) => String(p.type) === String(task?.prefixId)
      );
      const prefixLabel = prefixMatch?.message || task?.prefix?.message || task?.prefixName || '-';

      const rowEmployeeId =
        task?.employeeId !== null && task?.employeeId !== undefined ? task.employeeId : '';

      return {
        id: task.id,
        employeeId: rowEmployeeId,
        dueDate: dueMoment ? dueMoment.format(DATE_FORMATS.MDY) : '-',
        _dueDate: dueMoment ? dueMoment.valueOf() : -1,
        prefix: prefixLabel,
        name: task?.name || '',
        description: helpers
          .stripHtml(task?.description || '')
          .replace(/&nbsp;/g, ' ')
          .trim(),
        status: statusDisplayForWorkOrder(task),
        priority: helpers.priorityToText(task?.priority),
        _priority: task?.priority ?? -1,
        teamMember: employeeLabel || 'Unassigned',
        created: createdMoment ? createdMoment.format(DATE_FORMATS.MDY) : '-',
        _created: createdMoment ? createdMoment.valueOf() : -1,
      };
    });
  }, [localTasks, employees, timeZone, filterValues, taskPrefixList]);

  const paginationInfo = useMemo(
    () => ({
      total: workOrderTotal,
      page: workOrderPage,
      pageSize: workOrderPageSize,
      goToPage: fetchWorkOrders,
    }),
    [workOrderTotal, workOrderPage, workOrderPageSize, fetchWorkOrders]
  );

  const rowClickInfo = useMemo(() => {
    if (!onOpenTask) return undefined;
    return {
      callback: (row: Record<string, any>) => {
        const taskId = row?.id;
        const fullTask = localTasks.find((task: any) => task.id === taskId);
        onOpenTask(fullTask || row);
      },
    };
  }, [onOpenTask, localTasks]);

  return (
    <InlineEditTable
      api=""
      columns={tableColumns}
      filterValues={filterValues}
      rows={rows}
      headerActions={headerActions}
      paginationInfo={paginationInfo}
      rowClickInfo={rowClickInfo}
      highlightedRow={highlightedRow}
      isLoading={isLoading}
      onCellCommit={handleCellCommit}
      entryRow={
        canAddWorkOrder
          ? {
              onSubmit: handleCreateWorkOrder,
              render: (ctx) => <QuickEntryRow options={columnOptions} {...ctx} />,
              resetToken: entryResetToken,
            }
          : undefined
      }
      sortState={sortQuery ?? undefined}
      onSort={handleSort}
    />
  );
}

export default WorkOrderTable;
